import { NavLink, useLocation } from 'react-router-dom';
import { useLibrary } from '@/hooks/useLibrary';
import { useToast } from '@/context/ToastProvider';
import type { LibraryKey } from '@/types/anime';

interface SidebarProps {
	isOpen: boolean;
	onNavigate: () => void;
}

/**
 * `search` được so khớp riêng: NavLink mặc định bỏ qua query string, nên
 * "Mới cập nhật" (/duyet) và "Mới thêm" (/duyet?sort=_id-desc) sẽ cùng
 * sáng nếu chỉ dựa vào pathname.
 */
const DISCOVER_LINKS = [
	{ to: '/', icon: '⌂', label: 'Trang chủ', search: '' },
	{ to: '/duyet?sort=_id-desc', icon: '✦', label: 'Mới thêm', search: '?sort=_id-desc' },
	{ to: '/duyet', icon: '◷', label: 'Mới cập nhật', search: '' },
	{ to: '/the-loai', icon: '▦', label: 'Thể loại', search: null },
];

const LIBRARY_LINKS: Array<{ to: string; icon: string; label: string; key: LibraryKey }> = [
	{ to: '/thu-vien/favorites', icon: '♡', label: 'Yêu thích', key: 'favorites' },
	{ to: '/thu-vien/watchlist', icon: '☷', label: 'Danh sách xem', key: 'watchlist' },
	{ to: '/thu-vien/history', icon: '◉', label: 'Lịch sử xem', key: 'history' },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
	`relative flex items-center gap-3 rounded-[11px] px-3.5 py-2.5 transition duration-200 ${
		isActive
			? 'text-white bg-linear-to-r from-accent/12 to-iris/12 shadow-[inset_3px_0_var(--color-accent)]'
			: 'text-muted hover:text-white hover:bg-linear-to-r hover:from-accent/12 hover:to-iris/12'
	}`;

export function Sidebar({ isOpen, onNavigate }: SidebarProps) {
	const { counts, continueWatching } = useLibrary();
	const { notify } = useToast();
	const location = useLocation();

	const discoverPathname = (to: string) => to.split('?')[0] ?? to;

	return (
		<aside
			id="app-sidebar"
			className={`fixed top-0 z-40 h-screen w-[245px] shrink-0 overflow-y-auto border-r border-line bg-bg/85 px-4.5 py-6 backdrop-blur-[18px] transition-[left] duration-300 scrollbar-slim min-[700px]:sticky min-[700px]:left-0 min-[1100px]:w-[245px] ${
				isOpen ? 'left-0' : '-left-[245px] min-[700px]:left-0'
			}`}
		>
			<div className="mx-3 mb-9 flex items-center gap-2.5 text-[21px] font-extrabold">
				<span className="grid size-[35px] place-items-center rounded-xl bg-linear-to-br from-accent to-iris text-[15px] text-white shadow-[0_7px_22px_rgb(255_92_168/0.27)]">
					▶
				</span>
				Neko<span className="text-accent">Stream</span>
			</div>

			<div className="px-3 pb-2.5 text-[11px] font-bold tracking-[1.3px] text-faint uppercase">
				Khám phá
			</div>
			<nav className="mb-7 grid gap-1.5">
				{DISCOVER_LINKS.map((link) => {
					const pathname = discoverPathname(link.to);
					const isActive =
						location.pathname === pathname &&
						(link.search === null || location.search === link.search);

					return (
						<NavLink
							key={link.to}
							to={link.to}
							className={linkClass({ isActive })}
							onClick={onNavigate}
						>
							<i className="w-5 text-center text-lg not-italic">{link.icon}</i>
							{link.label}
						</NavLink>
					);
				})}
			</nav>

			<div className="px-3 pb-2.5 text-[11px] font-bold tracking-[1.3px] text-faint uppercase">
				Thư viện
			</div>
			<nav className="grid gap-1.5">
				{LIBRARY_LINKS.map((link) => (
					<NavLink
						key={link.to}
						to={link.to}
						className={linkClass}
						onClick={onNavigate}
					>
						<i className="w-5 text-center text-lg not-italic">{link.icon}</i>
						<span className="flex-1">{link.label}</span>
						<b className="text-accent">{counts[link.key]}</b>
					</NavLink>
				))}
			</nav>

			<div className="mx-1 mt-4.5 rounded-[13px] border border-line bg-white/[0.02] p-3.5">
				<strong className="block text-[13px]">
					Đang xem tiếp
					<span className="float-right text-accent">
						{continueWatching?.progress
							? `${Math.round(continueWatching.progress)}%`
							: ''}
					</span>
				</strong>
				{continueWatching ? (
					<NavLink
						to={`/xem/${continueWatching.slug}${
							continueWatching.lastEpisodeSlug
								? `/${continueWatching.lastEpisodeSlug}`
								: ''
						}`}
						onClick={onNavigate}
						className="mt-1 block truncate text-[11px] text-muted hover:text-ink"
					>
						{continueWatching.title}
						{continueWatching.lastEpisodeName
							? ` · ${continueWatching.lastEpisodeName}`
							: ''}
					</NavLink>
				) : (
					<small className="mt-1 block text-[11px] text-muted">
						Chưa có lịch sử xem
					</small>
				)}
				<div className="mt-2.5 h-1 overflow-hidden rounded-full bg-white/10">
					<span
						className="block h-full rounded-[inherit] bg-linear-to-r from-accent to-aqua transition-[width] duration-500"
						style={{ width: `${continueWatching?.progress ?? 0}%` }}
					/>
				</div>
			</div>

			<div className="mt-10 rounded-[15px] border border-line bg-linear-to-br from-[#201938] to-[#171426] p-4">
				<strong className="mb-1 block">Tham gia cộng đồng</strong>
				<small className="block text-xs text-muted">
					Nhận thông báo anime mới mỗi ngày.
				</small>
				<button
					type="button"
					onClick={() => notify('Tính năng đăng ký đang được mở!', 'info')}
					className="mt-3.5 w-full rounded-[9px] bg-accent py-2.5 font-bold transition active:scale-[0.97]"
				>
					Tham gia ngay
				</button>
			</div>
		</aside>
	);
}
